import React from 'react';
import { motion } from 'framer-motion';
import { FaStar } from 'react-icons/fa';
import Container from '../../../components/ui/Container';
import OptimizedImage from '../../../components/ui/OptimizedImage';
import ScrollReveal from '../../../components/ui/ScrollReveal';
import SectionHeading from '../../../components/ui/SectionHeading';

const TestimonialsSection = ({ data }) => {
  if (!data) return null;

  return (
    <section className="py-section" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
      <Container>
        <ScrollReveal>
          <SectionHeading
            tag="Success Stories"
            title={data.title}
            subtitle={data.description}
          />
        </ScrollReveal>

        <div className="columns-1 md:columns-2 gap-6 space-y-6">
          {data.testimonials.map((testimonial, i) => (
            <TestimonialCard key={i} testimonial={testimonial} index={i} />
          ))}
        </div>
      </Container>
    </section>
  );
};

const TestimonialCard = ({ testimonial, index }) => {
  const isOdd = index % 2 !== 0;

  return (
    <motion.div
      initial={{ opacity: 0, x: isOdd ? 30 : -30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      className="break-inside-avoid"
    >
      <div className="relative rounded-2xl overflow-hidden glass border border-[var(--color-border-card)] shadow-card hover:shadow-card-hover transition-all duration-300 p-6 group hover:border-brand-400/30">
        {/* Gradient border on hover */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-2xl"
          style={{
            background: 'linear-gradient(135deg, rgba(83,150,189,0.1), transparent, rgba(100,178,255,0.1))',
          }}
        />

        {/* Screenshot testimonial */}
        {testimonial.screenshotImage && (
          <OptimizedImage
            src={testimonial.screenshotImage}
            alt="Client testimonial"
            className="w-full h-auto rounded-xl mb-4"
            wrapperClassName="rounded-xl overflow-hidden mb-4"
            aspectRatio="4/3"
          />
        )}

        {/* Video testimonial */}
        {testimonial.videoTestimony && (
          <div className="relative w-full mb-4 rounded-xl overflow-hidden">
            <iframe
              className="w-full aspect-video rounded-xl"
              src={`https://www.youtube.com/embed/${testimonial.videoTestimony.videoId}?autoplay=1&mute=1&loop=1&playlist=${testimonial.videoTestimony.videoId}`}
              title="Client Testimonial"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        )}

        {/* Quote icon */}
        {!testimonial.videoTestimony && !testimonial.screenshotImage && (
          <div className="mb-3">
            <svg className="w-8 h-8" fill="url(#quoteGrad)" viewBox="0 0 24 24">
              <defs>
                <linearGradient id="quoteGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#5396BD" />
                  <stop offset="100%" stopColor="#64B2FF" />
                </linearGradient>
              </defs>
              <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10H14.017zM0 21v-7.391c0-5.704 3.731-9.57 8.983-10.609L9.978 5.151C7.546 6.068 5.983 8.789 5.983 11H10v10H0z" />
            </svg>
          </div>
        )}

        <p className="text-sm leading-relaxed mb-4 flex-1 text-[var(--color-text-secondary)]">
          {testimonial.testimony}
        </p>

        <div className="flex items-center gap-3 pt-4 border-t border-[var(--color-border-default)]">
          <OptimizedImage
            src={testimonial.user.profileImage}
            alt={`${testimonial.user.firstName} ${testimonial.user.lastName}`}
            className="w-10 h-10 rounded-full"
            wrapperClassName="w-10 h-10 rounded-full overflow-hidden flex-shrink-0"
            objectFit="cover"
            aspectRatio="1/1"
          />
          <div>
            <p className="text-sm font-semibold text-[var(--color-text-primary)]">
              {testimonial.user.firstName} {testimonial.user.lastName}
            </p>
            <p className="text-xs text-[var(--color-text-muted)]">
              {testimonial.user.designation}
            </p>
          </div>
          <div className="ml-auto flex gap-0.5">
            {[1, 2, 3, 4, 5].map((s) => (
              <motion.span
                key={s}
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 + s * 0.08 }}
              >
                <FaStar className="w-3.5 h-3.5 text-yellow-400" />
              </motion.span>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TestimonialsSection;
